import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { AffiliateDisclosure } from '../components/AffiliateDisclosure';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Seo } from '../components/Seo';
import { productSchema, breadcrumb } from '../lib/structuredData';
import { formatPrice } from '../lib/currency';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { openDeal } from '../lib/deals';
import {
  Star, Clock, Check, X, ExternalLink, ArrowLeft, ShieldCheck, Info, Sparkles, AlertCircle,
} from 'lucide-react';

export default function ExperienceDetail() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { currency } = useApp();
  const city = params.get('city') || '';
  const qPrice = params.get('price');
  const qDur = params.get('dur');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const { data: res } = await api.experienceDetail(code, { currency, city, ...(qPrice ? { price: qPrice } : {}) });
      setData(res); setActiveImg(0);
    } catch (e) {
      console.error('Experience detail failed', e); setError(true);
    } finally { setLoading(false); }
  }, [code, currency, city, qPrice]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [data]);

  useEffect(() => { load(); }, [load]);

  const price = (data?.price_inr ?? (qPrice ? Number(qPrice) : null));
  const durationHours = data?.duration_hours ?? (qDur ? Number(qDur) : null);
  const expDesc = data ? (data.description || `Book ${data.title}${city ? ` in ${city}` : ''} with instant confirmation. Compare and book on FlightDeals.in.`).slice(0, 300) : '';
  const expJsonLd = data ? [
    productSchema({
      name: data.title,
      description: expDesc,
      image: data.images?.[0] || data.image,
      path: `/experiences/${code}`,
      price,
      rating: data.rating,
      reviewCount: data.review_count,
    }),
    breadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Experiences', path: '/experiences' },
      { name: data.title },
    ]),
  ] : null;

  const book = () => {
    if (!data) return;
    openDeal({
      type: 'experience',
      offer: {
        offer_id: data.offer_id || `ex_viator_${code}`,
        product_code: data.product_code || code,
        type: 'experience', partner_id: 'viator', program_id: 'viator-partner',
        deep_link: data.deep_link, affiliate_url: data.affiliate_url || data.deep_link,
        destination: data.destination || city, title: data.title,
        is_demo: false, is_mock: false,
      },
      surface: 'experience_detail', placement: 'detail', source: `/experiences/${code}`,
    });
  };

  return (
    <Layout>
      {data && <Seo title={data.title} description={expDesc} path={`/experiences/${code}`}
        image={data.images?.[0] || data.image} type="product" jsonLd={expJsonLd} />}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-sm mb-4" data-testid="experience-detail-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to experiences
        </Button>

        {loading && (
          <div className="grid gap-6 lg:grid-cols-3" data-testid="experience-detail-loading">
            <div className="lg:col-span-2 space-y-4">
              <Skeleton className="h-80 w-full rounded-2xl" />
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-24 w-full" />
            </div>
            <Skeleton className="h-64 w-full rounded-2xl" />
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-border bg-card p-10 text-center" data-testid="experience-detail-error">
            <AlertCircle className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <h2 className="font-semibold text-lg">We couldn't load this experience</h2>
            <p className="text-sm text-muted-foreground mt-1">It may be temporarily unavailable. Please try again.</p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <Button onClick={load} className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="experience-detail-retry">Try again</Button>
              <Button variant="outline" onClick={() => navigate('/experiences')}>Browse experiences</Button>
            </div>
          </div>
        )}

        {!loading && !error && data && (
          <div className="grid gap-6 lg:grid-cols-3" data-testid="experience-detail">
            {/* Left: gallery + content */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <div className="rounded-2xl overflow-hidden border border-border bg-card">
                  <img src={data.images?.[activeImg] || data.image} alt={data.title}
                    className="w-full h-72 sm:h-96 object-cover" data-testid="experience-detail-hero-image" />
                </div>
                {data.images?.length > 1 && (
                  <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar" data-testid="experience-detail-thumbs">
                    {data.images.slice(0, 6).map((src, i) => (
                      <button key={src} onClick={() => setActiveImg(i)}
                        className={`h-16 w-24 shrink-0 rounded-lg overflow-hidden border-2 transition-colors ${i === activeImg ? 'border-accent' : 'border-transparent hover:border-border'}`}>
                        <img src={src} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h1 className="font-display text-2xl sm:text-3xl font-semibold leading-tight" data-testid="experience-detail-title">{data.title}</h1>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                  {data.rating ? (
                    <span className="inline-flex items-center gap-1" data-testid="experience-detail-rating">
                      <Star className="h-4 w-4 fill-amber-400 text-amber-400" /> {data.rating}
                      <span className="text-xs">({(data.review_count || 0).toLocaleString('en-IN')} reviews)</span>
                    </span>
                  ) : null}
                  {durationHours ? <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {durationHours}h</span> : null}
                  {city ? <Badge variant="outline" className="rounded-full">{city}</Badge> : null}
                  {data.confirmation_type === 'INSTANT' ? <Badge className="rounded-full bg-accent/12 text-accent border-accent/30">Instant confirmation</Badge> : null}
                </div>
              </div>

              {data.description ? (
                <div>
                  <h2 className="font-semibold text-base mb-1">Overview</h2>
                  <p className="text-sm text-muted-foreground whitespace-pre-line" data-testid="experience-detail-description">{data.description}</p>
                </div>
              ) : null}

              {data.highlights?.length > 0 && (
                <div>
                  <h2 className="font-semibold text-base mb-2 flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-accent" /> Highlights</h2>
                  <ul className="space-y-1.5">
                    {data.highlights.map((h) => (
                      <li key={h} className="text-sm text-muted-foreground flex gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {h}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid gap-6 sm:grid-cols-2">
                {data.inclusions?.length > 0 && (
                  <div data-testid="experience-detail-inclusions">
                    <h3 className="font-semibold text-sm mb-2">What's included</h3>
                    <ul className="space-y-1.5">
                      {data.inclusions.map((x) => <li key={x} className="text-sm text-muted-foreground flex gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {x}</li>)}
                    </ul>
                  </div>
                )}
                {data.exclusions?.length > 0 && (
                  <div data-testid="experience-detail-exclusions">
                    <h3 className="font-semibold text-sm mb-2">Not included</h3>
                    <ul className="space-y-1.5">
                      {data.exclusions.map((x) => <li key={x} className="text-sm text-muted-foreground flex gap-2"><X className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" /> {x}</li>)}
                    </ul>
                  </div>
                )}
              </div>

              {data.additional_info?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-sm mb-2 flex items-center gap-1.5"><Info className="h-4 w-4" /> Good to know</h3>
                  <ul className="space-y-1.5">
                    {data.additional_info.map((x) => <li key={x} className="text-xs text-muted-foreground flex gap-2">• {x}</li>)}
                  </ul>
                </div>
              )}
            </div>

            {/* Right: sticky booking card */}
            <aside className="lg:sticky lg:top-24 h-fit">
              <div className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="experience-detail-booking">
                <div>
                  {price ? (
                    <>
                      <p className="text-xs text-muted-foreground">from</p>
                      <p className="text-2xl font-semibold tabular" data-testid="experience-detail-price">{formatPrice(price, currency)}</p>
                      <p className="text-[11px] text-muted-foreground">per person · indicative price</p>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">See live pricing & availability on Viator.</p>
                  )}
                </div>

                {data.cancellation?.text ? (
                  <div className={`text-xs flex gap-2 ${data.cancellation.free ? 'text-emerald-600' : 'text-muted-foreground'}`} data-testid="experience-detail-cancellation">
                    {data.cancellation.free ? <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" /> : <Info className="h-4 w-4 shrink-0 mt-0.5" />}
                    <span>{data.cancellation.text}</span>
                  </div>
                ) : null}

                <Button onClick={book} className="w-full h-12 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold text-base" data-testid="book-on-viator-button">
                  Book on Viator <ExternalLink className="h-4 w-4 ml-1.5" />
                </Button>
                <p className="text-[11px] text-muted-foreground text-center" data-testid="experience-detail-partner-note">
                  You'll complete your booking securely on Viator. Prices & availability are confirmed there.
                </p>
                <AffiliateDisclosure variant="footer" />
              </div>
            </aside>
          </div>
        )}
      </section>
    </Layout>
  );
}
